import { v4 as uuidv4 } from 'uuid';
import { addProbedQuestion, clearProbedQuestions, updateProbedQuestion, setVariableMentions } from '../actions';
import { ProbedQuestionModel } from '../../models/ProbedQuestionModel';
import { getAIProbe, generateProbedQuestions } from '../../services/AIProbeService';
import { ThunkActionType } from '../actions/types';
import { QuestionType } from '../../models/enums/QuestionType';

/**
 * Thunk to fetch a probed question from the AI service based on an answer and context
 */
export const fetchProbedQuestion = (
    answer: string,
    mainVariableId: string,
    variableId: string,
    name: string,
    questionId: string | number,
    context: any = {},
    order: number = 1,
    matrix?: string
): ThunkActionType<Promise<ProbedQuestionModel | null>> => {
    return async (dispatch) => {
        try {
            // Get the probed question from the AI service using dispatch
            const probeResult = await dispatch(getAIProbe(answer, context));
            
            if (probeResult) {
                // Create a new probed question model
                const probedQuestion: ProbedQuestionModel = {
                    id: uuidv4(),
                    mainVariableId,
                    variableId,
                    name,
                    questionText: probeResult.questionText,
                    order,
                    questionId: String(questionId), // Convert to string to ensure consistency
                    matrix,
                    shouldContinue: probeResult.shouldContinue
                };
                
                // Dispatch the action to add the probed question
                dispatch(addProbedQuestion(probedQuestion));
                
                // Set a default space mention for this probed question based on question type
                if (context.questionType === QuestionType.OpenEndAnswer) {
                    // For OpenEndAnswer, use code "0" with value in openEnd property
                    dispatch(setVariableMentions(variableId, (mentions) => mentions.set("0", " ")));
                } else {
                    // For TextAnswer and other types, set value directly
                    dispatch(setVariableMentions(variableId, (mentions) => mentions.set(" ")));
                }
                
                return probedQuestion;
            }
            
            return null;
        } catch (error) {
            console.error('Error fetching probed question:', error);
            return null;
        }
    };
};

/**
 * Thunk to generate multiple probed questions in sequence
 * Each probed question uses the previous one's answer as context
 */
export const generateProbedQuestionsSequence = (
    initialAnswer: string,
    mainVariableId: string,
    variableId: string,
    name: string,
    questionId: string | number,
    context: any = {},
    matrix?: string,
    nextOrder?: number,
    settings?: { [key: string]: any }
): ThunkActionType<Promise<boolean>> => {
    return async (dispatch) => {
        try {
            // Only clear if we're generating the first set and nextOrder is not provided
            // This ensures we don't clear questions when adding to an existing sequence
            if (!nextOrder) {
                dispatch(clearProbedQuestions(mainVariableId));
            }
            
            // Extract the original question text from the context if available
            const originalQuestionText = context?.questionText || 
                                        context?.settings?.QuestionText || 
                                        "Please share your thoughts on this topic";
            
            // Call the API to generate probed questions, now with dispatch
            const probedQuestions = await dispatch(generateProbedQuestions(
                initialAnswer,
                mainVariableId,
                name,
                String(questionId),
                originalQuestionText,
                context,
                settings
            ));
            
            // Override the order if nextOrder is provided
            if (nextOrder && probedQuestions.length > 0) {
                probedQuestions[0].order = nextOrder;
                
                // Update the ID to reflect the new order
                const parts = probedQuestions[0].id.split('_');
                parts[3] = nextOrder.toString();
                probedQuestions[0].id = parts.join('_');
            }
            
            // Check if we should stop generating questions based on API response
            const shouldContinue = probedQuestions.length > 0 && 
                                   probedQuestions[0].shouldContinue !== false;

            // Add each probed question to the store
            probedQuestions.forEach(questionData => {
                const probedQuestion: ProbedQuestionModel = {
                    id: questionData.id,
                    mainVariableId,
                    variableId: questionData.id,
                    name: questionData.id,
                    questionText: questionData.questionText,
                    order: questionData.order,
                    questionId: String(questionId),
                    matrix,
                    answer: "",
                    shouldContinue: questionData.shouldContinue,
                    settings:settings
                };
                
                // Add the probed question
                dispatch(addProbedQuestion(probedQuestion));
            });
            
            // Filter out marker probes that have no question text so we don't create empty mentions
            const actionableProbes = probedQuestions.filter(q => q.questionText && q.questionText.trim() !== '');

            // Legacy consolidated format (belt and suspenders)
            const matrixVal = matrix ? parseInt(matrix, 10) : undefined;
            dispatch(setVariableMentions(
                `${name}_AI_PROBING`,
                mentions => {
                    if (!nextOrder) { mentions.clear(); }
                    actionableProbes.forEach(questionData => {
                        mentions.add(" ", questionData.questionText);
                    });
                    return mentions;
                },
                matrixVal
            ));

            // Probe 1 = unnumbered vars, Probe 2 = _1 suffix
            actionableProbes.forEach((questionData) => {
                const probeOrder = nextOrder ? nextOrder : questionData.order;
                const qSuffix = probeOrder === 1 ? '_AI_PROBING_QUESTIONS' : `_AI_PROBING_QUESTIONS_${probeOrder - 1}`;
                const aSuffix = probeOrder === 1 ? '_AI_PROBING_ANSWERS' : `_AI_PROBING_ANSWERS_${probeOrder - 1}`;
                dispatch(setVariableMentions(`${name}${qSuffix}`, mentions => mentions.set(questionData.questionText, questionData.questionText), matrixVal));
                dispatch(setVariableMentions(`${name}${aSuffix}`, mentions => mentions.set(" ", " "), matrixVal));
            });

            // Clear probe 2 variables if only 1 probe was generated
            const maxProbeOrder = nextOrder ? nextOrder : (actionableProbes.length > 0 ? actionableProbes[actionableProbes.length - 1].order : 0);
            if (maxProbeOrder < 2) {
                dispatch(setVariableMentions(`${name}_AI_PROBING_QUESTIONS_1`, mentions => mentions.clear(), matrixVal));
                dispatch(setVariableMentions(`${name}_AI_PROBING_ANSWERS_1`, mentions => mentions.clear(), matrixVal));
            }

            // Return whether more questions can be generated
            return shouldContinue;
        } catch (error) {
            console.error('Error generating probed questions sequence:', error);
            return false;
        }
    };
};

/**
 * Thunk to clear all probed questions after a specific order number
 * This is useful when a user changes an answer and we need to regenerate the follow-up questions
 */
export const clearFollowingProbedQuestions = (
    mainVariableId: string,
    afterOrder: number = 0
): ThunkActionType<Promise<boolean>> => {
    return async (dispatch, getState) => {
        try {
            if (!getState) {
                console.error('getState is undefined in clearFollowingProbedQuestions');
                return false;
            }
            
            const state = getState();
            if (!state || !state.interview) {
                console.error('State or interview is undefined in clearFollowingProbedQuestions');
                return false;
            }
            
            // Use type assertion to safely access probedQuestions
            const interviewVars = state.interview.variables || {};
            const probedQuestions = (interviewVars as any).probedQuestions?.[mainVariableId] || [];
            
            // Get questions to keep (questions with order <= afterOrder)
            const questionsToKeep = probedQuestions.filter(q => q.order <= afterOrder);
            
            // Clear all questions for this variable
            dispatch(clearProbedQuestions(mainVariableId));
            
            // Re-add the questions we want to keep
            questionsToKeep.forEach(question => {
                dispatch(addProbedQuestion(question));
            });
            
            return true;
        } catch (error) {
            console.error('Error clearing following probed questions:', error);
            return false;
        }
    };
};

/**
 * Thunk to handle re-probing when a specific probed question answer changes
 * This clears all following questions and generates a new question if needed
 */
export const handleProbedQuestionChange = (
    mainVariableId: string,
    probedQuestionId: string,
    answer: string,
    settings
): ThunkActionType<Promise<boolean>> => {
    return async (dispatch, getState) => {
        try {
            if (!getState) {
                console.error('getState is undefined in handleProbedQuestionChange');
                return false;
            }
            
            const state = getState();
            if (!state || !state.interview) {
                console.error('State or interview is undefined in handleProbedQuestionChange');
                return false;
            }
            
            // Use type assertion to safely access probedQuestions
            const interviewVars = state.interview.variables || {};
            const allProbedQuestions = (interviewVars as any).probedQuestions?.[mainVariableId] || [];
            
            // Find the question that changed
            const changedQuestion = allProbedQuestions.find(q => q.id === probedQuestionId);
            if (!changedQuestion) {
                return false;
            }
            
            // Update the question with the new answer
            dispatch(updateProbedQuestion(mainVariableId, probedQuestionId, answer));
            
            // Clear all following questions - this is crucial to prevent duplicates
            await dispatch(clearFollowingProbedQuestions(mainVariableId, changedQuestion.order));
            
            // Get the base variable name for creating all variation names
            const changedQuestionName = changedQuestion.name;
            
            // Important: Get updated state after clearing following questions to avoid duplicates
            const updatedState = getState();
            const updatedVars = updatedState.interview.variables || {};
            const currentProbedQuestions = (updatedVars as any).probedQuestions?.[mainVariableId] || [];

            // Get any answered questions to add to the mentions
            const answeredQuestions = currentProbedQuestions
                .filter(q => q.answer && q.answer.trim() !== '')
                .sort((a, b) => a.order - b.order);

            // Get the matrix index if any
            const matrixIndex = changedQuestion.matrix ? parseInt(changedQuestion.matrix, 10) : undefined;

            // Legacy format (belt and suspenders)
            const legacyProbeVariableName = `${changedQuestionName}_AI_PROBING`;
            dispatch(setVariableMentions(
                legacyProbeVariableName,
                mentions => {
                    mentions.clear();
                    answeredQuestions.forEach(question => {
                        mentions.add(question.answer, question.questionText);
                    });
                    return mentions;
                },
                matrixIndex
            ));

            // Probe 1 = unnumbered vars, Probe 2 = _1 suffix
            answeredQuestions.forEach(question => {
                const qSuffix = question.order === 1 ? '_AI_PROBING_QUESTIONS' : `_AI_PROBING_QUESTIONS_${question.order - 1}`;
                const aSuffix = question.order === 1 ? '_AI_PROBING_ANSWERS' : `_AI_PROBING_ANSWERS_${question.order - 1}`;
                dispatch(setVariableMentions(`${changedQuestionName}${qSuffix}`, mentions => mentions.set(question.questionText, question.questionText), matrixIndex));
                dispatch(setVariableMentions(`${changedQuestionName}${aSuffix}`, mentions => mentions.set(question.answer, question.answer), matrixIndex));
            });

            // Clear probe 2 variables if only 1 probe answered
            const maxAnsweredOrder = answeredQuestions.length > 0 ? answeredQuestions[answeredQuestions.length - 1].order : 0;
            if (maxAnsweredOrder < 2) {
                dispatch(setVariableMentions(`${changedQuestionName}_AI_PROBING_QUESTIONS_1`, mentions => mentions.clear(), matrixIndex));
                dispatch(setVariableMentions(`${changedQuestionName}_AI_PROBING_ANSWERS_1`, mentions => mentions.clear(), matrixIndex));
            }
            
            // Only generate a new question if:
            // 1. The answer is not empty
            // 2. This is the last question in the sequence (to avoid duplicates)
            // 3. The shouldContinue flag is not explicitly set to false
            const isLastQuestion = currentProbedQuestions
                .every(q => q.order <= changedQuestion.order);
            
            if (
                answer.trim() !== '' && 
                isLastQuestion && 
                changedQuestion.shouldContinue !== false
            ) {
                // Get all previous Q&A pairs up to this question
                const previousQA = currentProbedQuestions
                    .filter(q => q.answer && q.answer.trim() !== '' && q.order <= changedQuestion.order)
                    .map(q => {
                        // Get the answer either from the current change or from the mention
                        let answerText = q.id === probedQuestionId ? answer : q.answer;
                        
                        return {
                            question: q.questionText,
                            answer: answerText
                        };
                    });
                
                // Set up the context for the next question
                const context = {
                    previousQA,
                    questionId: changedQuestion.questionId,
                    followUpTo: changedQuestion.id,
                    questionText: changedQuestion.questionText
                };
                
                // Generate the next question
                await dispatch(generateProbedQuestionsSequence(
                    answer,
                    mainVariableId,
                    changedQuestion.variableId,
                    changedQuestion.name,
                    changedQuestion.questionId,
                    context,
                    changedQuestion.matrix,
                    changedQuestion.order + 1,
                    settings
                ));
            }
            
            return true;
        } catch (error) {
            console.error('Error handling probed question change:', error);
            return false;
        }
    };
}; 